import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { api } from "@/lib/api";
import {
  TrendingUp,
  AlertOctagon,
  Building2,
  Layers,
  Award,
} from "lucide-react";

const PRIORITY_TONE = {
  critical: "#be123c",
  high: "#ea580c",
  medium: "#ca8a04",
  low: "#0d9488",
  unknown: "#a8a29e",
};

const STATUS_TONE = {
  open: "#dc2626",
  in_progress: "#f59e0b",
  resolved: "#059669",
  cancelled: "#a8a29e",
  unknown: "#78716c",
};

const PRIORITY_LABEL = {
  critical: "Crítica",
  high: "Alta",
  medium: "Média",
  low: "Baixa",
  unknown: "Sem prio.",
};

const STATUS_LABEL = {
  open: "Aberta",
  in_progress: "Em andamento",
  resolved: "Resolvida",
  cancelled: "Cancelada",
  unknown: "—",
};

const CATEGORY_LABEL = {
  abertura: "Abertura",
  fechamento: "Fechamento",
  appcc: "APPCC",
  limpeza: "Limpeza",
  recebimento: "Recebimento",
  operacao: "Operação",
  outros: "Outros",
};

const CATEGORY_COLORS = ["#065f46", "#0d9488", "#0891b2", "#7c3aed", "#ea580c", "#d97706", "#737373"];

function ChartCard({ title, hint, icon: Icon, action, children, testId }) {
  return (
    <Card
      className="p-5 rounded-xl border-stone-200 bg-white"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-800 flex items-center justify-center">
              <Icon className="h-4 w-4" />
            </div>
          )}
          <div>
            <h3 className="font-display font-bold tracking-tight">{title}</h3>
            {hint && (
              <p className="text-xs text-stone-500 font-body mt-0.5">{hint}</p>
            )}
          </div>
        </div>
        {action}
      </div>
      <div className="-mx-1">{children}</div>
    </Card>
  );
}

function Empty({ label }) {
  return (
    <div className="h-[220px] flex items-center justify-center text-sm text-stone-400 font-body">
      {label || "Sem dados no período"}
    </div>
  );
}

export default function DashboardAnalytics({ hideTopUnits = false }) {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/dashboard/analytics?days=${days}`)
      .then(({ data }) => setData(data))
      .catch((err) => console.error("analytics", err))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading && !data) {
    return (
      <Card className="p-6 rounded-xl border-stone-200 bg-white" data-testid="analytics-loading">
        <div className="h-[300px] flex items-center justify-center text-sm text-stone-400 font-body">
          Carregando indicadores…
        </div>
      </Card>
    );
  }
  if (!data) return null;

  const compliance = data.compliance_trend || [];
  const priorities = data.occurrences_by_priority || [];
  const statuses = data.occurrences_by_status || [];
  const topUnits = data.top_units || [];
  const categories = data.checklists_by_category || [];
  const auditTrend = (data.audit_score_trend || []).filter((r) => r.avg_score !== null);

  const hasCompliance = compliance.some((r) => r.completed > 0);
  const hasAudit = auditTrend.length > 0;
  const hasUnits = topUnits.length > 0;
  const hasCategories = categories.length > 0;
  const hasOccurrences = priorities.length > 0 || statuses.length > 0;

  // axis tick formatter "07/06"
  const dayTick = (iso) => {
    if (!iso || iso.length < 10) return iso;
    return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  };

  return (
    <section className="space-y-5" data-testid="analytics-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
            Análise
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight mt-1">
            Indicadores operacionais
          </h2>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger
            className="w-[150px] bg-white border-stone-300"
            data-testid="analytics-range"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 dias</SelectItem>
            <SelectItem value="14">Últimos 14 dias</SelectItem>
            <SelectItem value="30">Últimos 30 dias</SelectItem>
            <SelectItem value="60">Últimos 60 dias</SelectItem>
            <SelectItem value="90">Últimos 90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Row 1: compliance trend + audit score */}
      <div className="grid lg:grid-cols-3 gap-5">
        <ChartCard
          title="Tendência de conformidade"
          hint="Média diária de conformidade nas execuções concluídas"
          icon={TrendingUp}
          testId="chart-compliance"
        >
          {hasCompliance ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={compliance} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gComp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#065f46" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#065f46" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="day" tickFormatter={dayTick} stroke="#a8a29e" fontSize={11} />
                <YAxis stroke="#a8a29e" fontSize={11} domain={[0, 100]} unit="%" />
                <Tooltip
                  contentStyle={{
                    background: "white",
                    borderRadius: 8,
                    borderColor: "#e7e5e4",
                    fontSize: 12,
                  }}
                  labelFormatter={dayTick}
                  formatter={(v, n) =>
                    n === "compliance" ? [`${v}%`, "Conformidade"] : [v, "Concluídos"]
                  }
                />
                <Area
                  type="monotone"
                  dataKey="compliance"
                  stroke="#065f46"
                  strokeWidth={2}
                  fill="url(#gComp)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <Empty label="Nenhuma execução concluída neste período" />
          )}
        </ChartCard>

        <ChartCard
          title="Score de auditorias"
          hint="Score médio diário das auditorias concluídas"
          icon={Award}
          testId="chart-audit-score"
        >
          {hasAudit ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={auditTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="day" tickFormatter={dayTick} stroke="#a8a29e" fontSize={11} />
                <YAxis stroke="#a8a29e" fontSize={11} domain={[0, 100]} unit="%" />
                <Tooltip
                  contentStyle={{
                    background: "white",
                    borderRadius: 8,
                    borderColor: "#e7e5e4",
                    fontSize: 12,
                  }}
                  labelFormatter={dayTick}
                  formatter={(v) => [`${v}%`, "Score médio"]}
                />
                <Line
                  type="monotone"
                  dataKey="avg_score"
                  stroke="#d97706"
                  strokeWidth={2.5}
                  dot={{ fill: "#d97706", r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Empty label="Nenhuma auditoria concluída neste período" />
          )}
        </ChartCard>

        <ChartCard
          title="Categorias de checklist"
          hint="Distribuição por categoria nos templates ativos"
          icon={Layers}
          testId="chart-categories"
        >
          {hasCategories ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={categories.map((c) => ({
                    name: CATEGORY_LABEL[c.category] || c.category,
                    value: c.count,
                  }))}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={85}
                  innerRadius={45}
                  paddingAngle={2}
                >
                  {categories.map((c, i) => (
                    <Cell key={c.category || c.name || i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                  ))}
                </Pie>
                <Legend
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, fontFamily: "inherit" }}
                  verticalAlign="bottom"
                  height={28}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Empty label="Nenhum template criado" />
          )}
        </ChartCard>
      </div>

      {/* Row 2: occurrences priority/status + top units */}
      <div className="grid lg:grid-cols-3 gap-5">
        <ChartCard
          title="Ocorrências por prioridade"
          icon={AlertOctagon}
          testId="chart-occ-priority"
        >
          {hasOccurrences && priorities.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={priorities.map((p) => ({
                  name: PRIORITY_LABEL[p.priority] || p.priority,
                  value: p.count,
                  fill: PRIORITY_TONE[p.priority] || "#737373",
                }))}
                margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="name" stroke="#a8a29e" fontSize={11} />
                <YAxis stroke="#a8a29e" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "white",
                    borderRadius: 8,
                    borderColor: "#e7e5e4",
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty label="Nenhuma ocorrência registrada" />
          )}
        </ChartCard>

        <ChartCard
          title="Ocorrências por status"
          icon={AlertOctagon}
          testId="chart-occ-status"
        >
          {hasOccurrences && statuses.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={statuses.map((s) => ({
                    name: STATUS_LABEL[s.status] || s.status,
                    value: s.count,
                    _key: s.status,
                  }))}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={85}
                  label={(d) => d.value}
                  labelLine={false}
                >
                  {statuses.map((s) => (
                    <Cell key={s.status} fill={STATUS_TONE[s.status] || "#a8a29e"} />
                  ))}
                </Pie>
                <Legend
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, fontFamily: "inherit" }}
                  verticalAlign="bottom"
                  height={28}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Empty label="Nenhuma ocorrência registrada" />
          )}
        </ChartCard>

        {!hideTopUnits && (
          <ChartCard
            title="Unidades mais ativas"
            hint="Top 5 unidades por checklists concluídos"
            icon={Building2}
            testId="chart-top-units"
          >
            {hasUnits ? (
              <div className="space-y-3 pt-1">
                {topUnits.map((u, i) => (
                  <div key={u.unit || i} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="font-display font-semibold text-stone-900 truncate max-w-[70%]">
                        {u.unit}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-body text-xs text-stone-500">
                          {u.completed} execs
                        </span>
                        <Badge
                          variant="outline"
                          className="border-emerald-200 text-emerald-800 text-[10px]"
                        >
                          {u.compliance}%
                        </Badge>
                      </div>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-600 to-emerald-800"
                        style={{
                          width: `${Math.min(100, (u.completed / (topUnits[0]?.completed || 1)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty label="Nenhuma execução vinculada a unidade" />
            )}
          </ChartCard>
        )}
      </div>
    </section>
  );
}
